import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useRef } from 'react';

import type { FC, PropsWithChildren } from 'react';

import { HttpContext } from './http-context';
import { initializeHttpProvider } from './initializeHttpProvider';
import { setTokenResolver } from './utilsHttp';

export const HttpProvider: FC<PropsWithChildren> = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const value = initializeHttpProvider();
  const isRedirectingRef = useRef(false);

  const handleAuthError = useCallback(
    (error?: unknown) => {
      if (isRedirectingRef.current) return;
      isRedirectingRef.current = true;

      console.warn('Authentication token could not be refreshed. Redirecting to login...', error);

      const returnTo = window.location.pathname + window.location.search;
      loginWithRedirect({
        appState: {
          returnTo,
        },
      }).catch((err) => {
        console.error('Error executing loginWithRedirect after auth error:', err);
        isRedirectingRef.current = false;
      });
    },
    [loginWithRedirect],
  );

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      setTokenResolver(async () => {
        try {
          const token = await getAccessTokenSilently();
          return token ?? null;
        } catch (error) {
          console.error('Error resolving token dynamically in Ky:', error);
          handleAuthError(error);
          return null;
        }
      });
    } else if (!isAuthenticated && !isLoading) {
      setTokenResolver(null);
      isRedirectingRef.current = false;
    }

    return () => {
      setTokenResolver(null);
    };
  }, [isAuthenticated, isLoading, getAccessTokenSilently, handleAuthError]);

  return <HttpContext.Provider value={value}>{children}</HttpContext.Provider>;
};
